import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Label,
  Switch,
  TabList,
  Tab,
} from '@fluentui/react-components';
import { DocumentAddRegular } from '@fluentui/react-icons';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { useQuery } from '@apollo/client';
import { iconButtonStyles } from '../../app/common/styles/CommonStyleObjects';
import getColumns from './EntityTerminationList.config';
import Pagination from '../documentAnalysis/list/Pagination';
import { GET_MONITORING_QUEUE_LIST } from '../../utils/statApi/EntityTerminationApi';
import useUrlFiltering from '../../utils/hooks/useUrlFiltering';
import {
  EntityTerminationStage,
  IEntityTerminationStagePivotItem,
  IMonitoringQueueSearchState,
  MonitoringQueueQueryResult,
  TerminationStagesCounts,
} from './types';
import { IGenericFilterTag } from '../../utils/types/IGenericFilterTag';
import entityTerminationPivotItems from './EntityTerminationPivotItems';
import GenericFilterTagHandler from '../common/lists/GenericFilterTagHandler';
import MonitoringQueueFilters from './MonitoringQueueFilters';
import FeatureFlagged from '../common/featureFlagged/featureFlagged';
import FeatureFlagContext from '../../utils/featureFlags/FeatureFlagContext';
import FeatureFlags from '../../utils/featureFlags/FeatureFlags';
import TerminationIntakeRequestPanel from './intake/TerminationIntakeRequestPanel';
import { GET_LIFECYCLE_STATUSES_QUERY } from '../../utils/statApi/EntityStatusApi';
import IDropdownReference from '../../utils/types/IDropdownReference';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';

const defaultFilterObject: IMonitoringQueueSearchState = {
  companyCodes: undefined,
  countryCodes: undefined,
  activeRequestsOnly: true,
  entityTerminationStage: EntityTerminationStage.ELIGIBILITY_REVIEW,
  statControllerIds: undefined,
  terminationContactIds: undefined,
  celaTermContactIds: undefined,
  businessStakeholderContactIds: undefined,
  anticipatedTerminationFiscalYear: undefined,
  entityStatusIds: undefined,
  lifecycleStatusIds: undefined,
  terminationLifecycleStatusIds: undefined,
  legalInProcessFollowUpRequired: undefined,
  taskOwnerIds: undefined,
  taskAssigneeIds: undefined,
  primaryBlockerCategoryIds: undefined,
};

const arrayKeys = [
  'companyCodes',
  'countryCodes',
  'statControllerIds',
  'terminationContactIds',
  'celaTermContactIds',
  'businessStakeholderContactIds',
  'anticipatedTerminationFiscalYear',
  'entityStatusIds',
  'lifecycleStatusIds',
  'terminationLifecycleStatusIds',
  'taskOwnerIds',
  'taskAssigneeIds',
  'primaryBlockerCategoryIds',
];

const booleanKeys = ['activeRequestsOnly', 'legalInProcessFollowUpRequired'];

const useStyles = makeStyles({
  actionBar: { marginTop: '20px', marginBottom: '10px' },
  listControls: { marginLeft: 'auto' },
});

const MonitoringQueueListPage = (): JSX.Element => {
  const classes = useStyles();
  const featureFlags: FeatureFlags = useContext(FeatureFlagContext);
  const [searchState, setSearchState] = useUrlFiltering<IMonitoringQueueSearchState>({
    defaultFilterObject,
    arrayParameterKeys: arrayKeys,
    numberParameterKeys: ['lifecycleStatusIds'],
    boolParameterKeys: booleanKeys,
  });
  const [filterTagState, setFilterTagState] = useState<IGenericFilterTag[]>([]);
  const [terminationStagesCounts, setTerminationStagesCounts] = useState<TerminationStagesCounts>();
  const [selectedStage, setSelectedStage] = useState<string>(
    EntityTerminationStage.ELIGIBILITY_REVIEW,
  );
  const [isIntakePanelOpen, setIsIntakePanelOpen] = useState(false);
  const {
    data: lifecycleStatusData,
    loading: lifecycleStatusesLoading,
    error: lifecycleStatusesError,
  } = useQuery<{ lifecycleStatuses: IDropdownReference[] }>(GET_LIFECYCLE_STATUSES_QUERY, {
    variables: { keyword: '' },
    skip: !searchState.filter?.lifecycleStatusIds?.length,
  });

  const currentFilterTags = filterTagState.filter((tag) => tag.dataKey !== 'lifecycleStatusIds');
  if (searchState.filter?.lifecycleStatusIds?.length) {
    currentFilterTags.push({
      dataKey: 'lifecycleStatusIds',
      title: 'Lifecycle Status',
      values: searchState.filter.lifecycleStatusIds.map((id) => ({
        key: String(id),
        name:
          lifecycleStatusData?.lifecycleStatuses?.find((status) => status.id === id)?.fieldName ??
          String(id),
      })),
    });
  }

  useEffect(() => {
    const stageFromFilter = searchState.filter?.entityTerminationStage;
    if (!stageFromFilter) {
      setSelectedStage('All');
    } else {
      setSelectedStage(stageFromFilter);
    }
  }, [searchState.filter?.entityTerminationStage]);

  const dataSearchFilter = useMemo(() => {
    if (!searchState.filter) return searchState.filter;

    const {
      companyCodes,
      countryCodes,
      statControllerIds,
      terminationContactIds,
      celaTermContactIds,
      businessStakeholderContactIds,
      anticipatedTerminationFiscalYear,
      activeRequestsOnly,
      entityTerminationStage,
      entityStatusIds,
      lifecycleStatusIds,
      terminationLifecycleStatusIds,
      legalInProcessFollowUpRequired,
      taskOwnerIds,
      taskAssigneeIds,
      primaryBlockerCategoryIds,
    } = searchState.filter;

    const filterEmpty = (ids: string[]) => ids?.filter((id) => id) ?? [];

    return {
      companyCodes,
      countryCodes,
      statControllerIds: filterEmpty(statControllerIds),
      terminationContactIds: filterEmpty(terminationContactIds),
      celaTermContactIds: filterEmpty(celaTermContactIds),
      businessStakeholderContactIds: filterEmpty(businessStakeholderContactIds),
      anticipatedTerminationFiscalYear: filterEmpty(anticipatedTerminationFiscalYear),
      activeRequestsOnly,
      entityTerminationStage: entityTerminationStage === 'All' ? null : entityTerminationStage,
      entityStatusIds: entityStatusIds?.filter((id) => id) ?? [],
      lifecycleStatusIds: lifecycleStatusIds?.filter((id) => id) ?? [],
      terminationLifecycleStatusIds: terminationLifecycleStatusIds?.filter((id) => id) ?? [],
      legalInProcessFollowUpRequired,
      taskOwnerIds: filterEmpty(taskOwnerIds),
      taskAssigneeIds: filterEmpty(taskAssigneeIds),
      primaryBlockerCategoryIds: primaryBlockerCategoryIds?.filter((id) => id) ?? [],
    };
  }, [
    searchState.filter?.companyCodes,
    searchState.filter?.countryCodes,
    searchState.filter?.statControllerIds,
    searchState.filter?.terminationContactIds,
    searchState.filter?.celaTermContactIds,
    searchState.filter?.businessStakeholderContactIds,
    searchState.filter?.activeRequestsOnly,
    searchState.filter?.entityTerminationStage,
    searchState.filter?.anticipatedTerminationFiscalYear,
    searchState.filter?.entityStatusIds,
    searchState.filter?.lifecycleStatusIds,
    searchState.filter?.terminationLifecycleStatusIds,
    searchState.filter?.legalInProcessFollowUpRequired,
    searchState.filter?.taskOwnerIds,
    searchState.filter?.taskAssigneeIds,
    searchState.filter?.primaryBlockerCategoryIds,
  ]);

  const updateStateResetPagination = (newState: IMonitoringQueueSearchState): void => {
    setSearchState({ filter: newState });
  };

  const onFilterChange = (filterTags: IGenericFilterTag[]): void => {
    const newFilteredState: IMonitoringQueueSearchState = {
      ...searchState.filter,
      companyCodes:
        filterTags
          .find((f) => f.dataKey === 'company')
          ?.values.map((value) => value.name.split('- ')[0]) ?? undefined,
      countryCodes:
        filterTags
          .find((f) => f.dataKey === 'country')
          ?.values.map((value) => value.key as string) ?? undefined,
      statControllerIds:
        filterTags
          .find((f) => f.dataKey === 'controller')
          ?.values.map((value) => value.key as string) ?? undefined,
      terminationContactIds:
        filterTags
          .find((f) => f.dataKey === 'terminationContact')
          ?.values.map((value) => value.key as string) ?? undefined,
      celaTermContactIds:
        filterTags
          .find((f) => f.dataKey === 'celaTermContact')
          ?.values.map((value) => value.key as string) ?? undefined,
      businessStakeholderContactIds:
        filterTags
          .find((f) => f.dataKey === 'businessStakeholderContact')
          ?.values.map((value) => value.key as string) ?? undefined,
      anticipatedTerminationFiscalYear:
        filterTags
          .find((f) => f.dataKey === 'anticipatedTerminationFiscalYear')
          ?.values.map((value) => value.name as string) ?? undefined,
      entityStatusIds:
        filterTags
          .find((f) => f.dataKey === 'entityStatusIds')
          ?.values.map((value) => Number(value.key)) ?? undefined,
      lifecycleStatusIds:
        filterTags
          .find((f) => f.dataKey === 'lifecycleStatusIds')
          ?.values.map((value) => Number(value.key)) ?? undefined,
      terminationLifecycleStatusIds:
        filterTags
          .find((f) => f.dataKey === 'terminationLifecycleStatusId')
          ?.values.map((value) => Number(value.key)) ?? undefined,
      legalInProcessFollowUpRequired:
        filterTags
          .find((f) => f.dataKey === 'legalInProcessFollowUpRequired')
          ?.values.map((value) => value.key === 'true')[0] ?? undefined,
      taskOwnerIds:
        filterTags
          .find((f) => f.dataKey === 'taskOwner')
          ?.values.map((value) => value.key as string) ?? undefined,
      taskAssigneeIds:
        filterTags
          .find((f) => f.dataKey === 'taskAssignee')
          ?.values.map((value) => value.key as string) ?? undefined,
      primaryBlockerCategoryIds:
        filterTags
          .find((f) => f.dataKey === 'primaryBlockerCategoryIds')
          ?.values.map((value) => Number(value.key)) ?? undefined,
    };
    setFilterTagState(filterTags);
    updateStateResetPagination(newFilteredState);
  };

  const onInactiveRequestsToggle = (checked: boolean): void => {
    const newState = {
      ...searchState.filter,
    };
    const newStructuredState = structuredClone(newState);
    newStructuredState.activeRequestsOnly = !checked;

    updateStateResetPagination(newStructuredState);
  };

  const onPivotChange = (tabValue?: string): void => {
    if (!tabValue) return;

    setSelectedStage(tabValue);

    const newFilter: IMonitoringQueueSearchState = structuredClone(
      searchState.filter ?? defaultFilterObject,
    );

    newFilter.entityTerminationStage =
      tabValue === 'All' ? null : (tabValue as EntityTerminationStage);

    setSearchState({ filter: newFilter });
  };

  const onCompletedQueryUpdate = (data: MonitoringQueueQueryResult): void => {
    setTerminationStagesCounts(data?.entityTerminationStageTotals);
  };

  const getTerminationStageURLSegment = (): string => {
    const foundPivotItem = entityTerminationPivotItems.find(
      (item: IEntityTerminationStagePivotItem) => item.itemKey === selectedStage,
    );
    return foundPivotItem ? foundPivotItem.urlSegment : 'overview';
  };

  const getTabHeaderText = (terminationStage: string): string => {
    let count = 0;

    switch (terminationStage) {
      case EntityTerminationStage.ACTIVE_INTEGRATING:
        count = terminationStagesCounts?.activeIntegratingCount ?? 0;
        break;
      case EntityTerminationStage.ACTIVE_WIND_DOWN:
        count = terminationStagesCounts?.activeWindDownCount ?? 0;
        break;
      case EntityTerminationStage.INTAKE:
        count = terminationStagesCounts?.intakeCount ?? 0;
        break;
      case EntityTerminationStage.ELIGIBILITY_REVIEW:
        count = terminationStagesCounts?.eligibilityReviewCount ?? 0;
        break;
      case EntityTerminationStage.BLOCKER:
        count = terminationStagesCounts?.blockerCount ?? 0;
        break;
      case EntityTerminationStage.LEGAL_IN_PROCESS:
        count = terminationStagesCounts?.legalInProcessCount ?? 0;
        break;
      case EntityTerminationStage.FINANCE_INCOMPLETE:
        count = terminationStagesCounts?.financeIncompleteCount ?? 0;
        break;
      case EntityTerminationStage.ELIGIBLE:
        count = terminationStagesCounts?.eligibleCount ?? 0;
        break;
      case 'All':
        count = terminationStagesCounts?.totalCount ?? 0;
        break;
      default:
        break;
    }

    return `${terminationStage} (${count})`;
  };

  const pivotViewNavigation = () => {
    const visiblePivotItems = entityTerminationPivotItems.filter(
      (item: IEntityTerminationStagePivotItem) =>
        item.itemKey !== EntityTerminationStage.INTAKE ||
        Boolean(featureFlags?.flags?.EntityTerminationIntake),
    );

    return (
      <TabList
        aria-label="Summarize Legal Entity Terminations results by workflow type"
        selectedValue={selectedStage}
        onTabSelect={(event, tabData) => onPivotChange(tabData.value as string)}
      >
        {visiblePivotItems.map((item: IEntityTerminationStagePivotItem) => (
          <Tab key={item.itemKey} value={item.itemKey} content={getTabHeaderText(item.itemKey)} />
        ))}
      </TabList>
    );
  };

  const NoResultsMessageBar = (data: MonitoringQueueQueryResult, loading: boolean): JSX.Element => {
    if (data?.monitoringQueueList?.totalCount === 0 && !loading) {
      return (
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>
            <span>No items found for current filters</span>
          </MessageBarBody>
        </MessageBar>
      );
    }
    return <></>;
  };

  const currentStageForColumns =
    selectedStage === 'All' ? null : (selectedStage as EntityTerminationStage);
  return (
    <>
      <LoadingErrorMessage
        loading={lifecycleStatusesLoading}
        error={lifecycleStatusesError}
        actionName="loading lifecycle statuses"
      />
      <Pagination
        columns={getColumns(
          'monitoring-queue',
          currentStageForColumns,
          getTerminationStageURLSegment(),
        )}
        query={GET_MONITORING_QUEUE_LIST}
        label="Legal Entity Terminations"
        queryResultKey="monitoringQueueList"
        defaultSortColumn="companyCode"
        searchDto={dataSearchFilter}
        onCompletedQueryUpdate={onCompletedQueryUpdate}
        pivotViewNavigation={pivotViewNavigation}
        noResultsMessageBar={NoResultsMessageBar}
      >
        <Stack horizontal verticalAlign="center">
          <FeatureFlagged flagName="EntityTerminationIntake">
            <>
              <Button
                appearance="transparent"
                icon={<DocumentAddRegular color={iconButtonStyles.color} />}
                className={classes.actionBar}
                onClick={() => setIsIntakePanelOpen(true)}
              >
                New Termination Intake Request
              </Button>
              {isIntakePanelOpen && (
                <TerminationIntakeRequestPanel closePanel={() => setIsIntakePanelOpen(false)} />
              )}
            </>
          </FeatureFlagged>
          <Stack horizontal verticalAlign="center" className={classes.listControls}>
            <Label htmlFor="showInactiveRequestsSwitch">Show Inactive Requests</Label>
            <Switch
              id="showInactiveRequestsSwitch"
              checked={!(searchState?.filter?.activeRequestsOnly ?? true)}
              onChange={(_, data) => onInactiveRequestsToggle(data.checked)}
              label={!(searchState?.filter?.activeRequestsOnly ?? true) ? 'On' : 'Off'}
            />
            <GenericFilterTagHandler onChange={onFilterChange} allFilterTags={currentFilterTags}>
              <MonitoringQueueFilters />
            </GenericFilterTagHandler>
          </Stack>
        </Stack>
      </Pagination>
    </>
  );
};

export default MonitoringQueueListPage;
